import React, { useState, useEffect, useMemo, use } from 'react';
import {
    Box, Typography, Paper, Grid, Chip, Stack, Avatar, IconButton,
    Tooltip, Snackbar, Alert, CircularProgress, Switch, FormControlLabel, Skeleton,
    useTheme,
    Divider,
    Button
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import Brightness4Icon from '@mui/icons-material/Brightness4'; // Moon icon for dark mode
import Brightness7Icon from '@mui/icons-material/Brightness7'; // Sun icon for light mode
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { transformToBTC } from '../../utils/helpers';
import { useBitcoinPrice } from '../../state/context/bitcoin.context';
import PNLCard from './PNLCard.component';


// Helper function to truncate addresses for display
const truncateAddress = (address, startChars = 4, endChars = 5) => {
    if (!address) return '';
    if (address.length <= startChars + endChars) return address;
    return `${ address.substring(0, startChars) }...${ address.substring(address.length - endChars) }`;
};

// UserHeader component to display user data
const UserHeader = ({ userId, isCollapsed = false, usePnlButton = true }) => {
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const [pnlCardOpen, setPnlCardOpen] = useState(false);
    const avatarSrc = `https://images.odin.fun/user/${ userId }`;
    const theme = useTheme();
    const navigate = useNavigate();
    const { price: btcPrice } = useBitcoinPrice();
    const { t } = useTranslation();


    // Fetch user data from the API
    useEffect(() => {
        const fetchUserData = async () => {
            if (!userId) {
                setError(t('user.userIdNotProvided'));
                setLoading(false);
                return;
            }

            setLoading(true);
            setError(null);
            try {
                const response = await fetch(`https://api.odin.fun/v1/user/${ userId }`);
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${ response.status }`);
                }
                const data = await response.json();
                setUserData(data);
            } catch (e) {
                console.error("Failed to fetch user data:", e);
                setError(t('user.failedToFetchUserData'));
            } finally {
                setLoading(false);
            }
        };

        fetchUserData();
    }, [userId]); // Re-fetch when userId changes

    // Function to copy text to clipboard
    const copyToClipboard = (text, fieldName) => {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        document.body.appendChild(textarea);
        textarea.select();
        try {
            document.execCommand('copy');
            setSnackbarMessage(t('user.copiedToClipboard', { fieldName }));
            setSnackbarSeverity('success');
        } catch (err) {
            console.error('Failed to copy: ', err);
            setSnackbarMessage(t('user.failedToCopy'));
            setSnackbarSeverity('error');
        }
        document.body.removeChild(textarea);
        setSnackbarOpen(true);
    };

    // Handle snackbar close
    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbarOpen(false);
    };

    // Show loading indicator
    if (loading) {
        if (!usePnlButton) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2 }}>
                    <CircularProgress />
                </Box>
            );
        }
        return (

            <Paper
                elevation={3}
                sx={{
                    pl: 2,
                    pr: 2,
                    width: '100%',
                    mx: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    p: { xs: 2, sm: 4 },
                }}
            >
                {/* Avatar Skeleton */}
                <Skeleton
                    variant="circular"
                    width={128}
                    height={128}
                    sx={{ mb: 2 }}
                />

                {/* Username Skeleton */}
                <Skeleton
                    variant="text"
                    width="60%"
                    height={40}
                    sx={{ mb: 1 }}
                />

                {/* Bio Skeleton */}
                <Skeleton
                    variant="text"
                    width="80%"
                    height={24}
                    sx={{ mb: 3 }}
                />

                {/* Grid of field skeletons */}
                <Grid container spacing={1} sx={{ width: '100%', mb: 3 }}>
                    {Array.from({ length: 8 }).map((_, index) => (
                        <Grid item xs={12} sm={6} key={index}>
                            <Box sx={{ textAlign: 'left' }}>
                                <Skeleton variant="text" width="40%" height={20} sx={{ mb: 0.5 }} />
                                <Skeleton variant="text" width="75%" height={24} />
                            </Box>
                        </Grid>
                    ))}
                </Grid>

                {/* Additional fields skeleton */}
                <Grid container spacing={1} sx={{ width: '100%' }}>
                    {Array.from({ length: 2 }).map((_, index) => (
                        <Grid item xs={12} sm={6} key={index}>
                            <Box sx={{ textAlign: 'left' }}>
                                <Skeleton variant="text" width="30%" height={20} sx={{ mb: 0.5 }} />
                                <Skeleton variant="text" width="60%" height={24} />
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Paper>
        );
    }

    // Show error message
    if (error) {
        return (

            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '100vh',
                    backgroundColor: theme.palette.background.default,
                    p: 3,
                    flexDirection: 'column', // Added for centering content and error
                }}
            >
                <Paper elevation={3} sx={{ p: 4, textAlign: 'center', borderRadius: '16px', backgroundColor: theme.palette.background.paper }}>
                    <Typography variant="h6" color="error" gutterBottom>
                        Error: {error}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        {t('user.couldNotLoadUserProfile')}
                    </Typography>
                </Paper>
            </Box>
        );
    }

    // If data is null after loading (e.g., user not found)
    if (!userData) {
        return (

            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '100vh',
                    backgroundColor: theme.palette.background.default,
                    p: 3,
                    flexDirection: 'column', // Added for centering content
                }}
            >
                <Paper elevation={3} sx={{ p: 4, textAlign: 'center', borderRadius: '16px', backgroundColor: theme.palette.background.paper }}>
                    <Typography variant="h6" color="warning" gutterBottom>
                        {t('user.noUserFound')}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        {t('user.userIdProvidedNoData')}
                    </Typography>
                </Paper>
            </Box>
        );
    }

    // Format date string
    const formattedCreatedAt = userData.created_at
        ? new Date(userData.created_at).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        })
        : 'N/A';

    return (
        <>
            <Paper
                elevation={usePnlButton ? 2 : 0}
                sx={{
                    pl: usePnlButton ? 2 : 0,
                    pr: usePnlButton ? 2 : 0,
                    width: '100%',
                    mx: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    backgroundColor: [`${ usePnlButton ? theme.palette.background.paper : 'transparent'}`],
                }}
            >
                <Box
                    sx={{ display: 'flex', alignItems: 'center', mb: 1, mt: 1, width: '100%', justifyContent: 'space-between' }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar
                            alt={userData.username || t('user.unknownUser')}
                            src={avatarSrc}
                            sx={{
                                width: 60,
                                height: 60,
                                mb: 2,
                                '&:hover': {
                                    transform: 'scale(1.05)',
                                    transition: 'transform 0.2s ease-in-out',
                                },
                                bgcolor:theme.palette.primary.main,
                                border: `2px solid #fff`,
                                boxShadow: theme.shadows[2],
                            }}
                        />
                        <Divider sx={{ mx: 1 }} />
                        <Typography variant="h6"
                            gutterBottom
                            sx={{
                                fontWeight: 'bold',
                                color: theme.palette.text.primary,
                                cursor: 'pointer',
                                '&:hover': {
                                    textDecoration: 'underline',
                                    color: theme.palette.primary.main
                                }
                            }}
                            onClick={() => window.open(`/users/${ userId }`)}
                        >
                            {userData.username || t('user.unknownUser')}
                        </Typography>
                    </Box>

                    {
                        usePnlButton && (<Button
                            variant="outlined"
                            size="small"
                            sx={{
                                borderColor: theme.palette.primary.main,
                                color: theme.palette.primary.main,
                                '&:hover': {
                                    backgroundColor: theme.palette.primary.main,
                                    color: theme.palette.primary.contrastText,
                                },
                                minWidth: 'auto',
                                px: 2,
                                ml: 2, // Add left margin for spacing
                            }}
                            onClick={() => {
                                setPnlCardOpen(true);
                            }}
                        >
                            PNL Card
                        </Button>)
                    }
                </Box>

                {
                    isCollapsed && (
                        <>
                            {userData.bio && (
                                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                                    {userData.bio}
                                </Typography>
                            )}

                            <Grid container spacing={1} sx={{ width: '100%', mb: 1 }}>
                                {/* Principal */}
                                <Grid item xs={12} sm={6}>
                                    <Box sx={{ textAlign: 'left' }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            {t('user.principalId')}:
                                        </Typography>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <Typography variant="body1" sx={{ wordBreak: 'break-all', }}>
                                                {truncateAddress(userData.principal, 4, 8) || t('user.notAvailable')}
                                            </Typography>
                                            {userData.principal && (
                                                <Tooltip title={t('user.copyPrincipalId')}>
                                                    <IconButton size="small" onClick={() => copyToClipboard(userData.principal, t('user.principalId'))} sx={{}}>
                                                        <ContentCopyIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Stack>
                                    </Box>
                                </Grid>

                                {/* Referral Earnings */}
                                <Grid item xs={12} sm={6}>
                                    <Box sx={{ textAlign: 'left' }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            {t('user.referralEarnings')}:
                                        </Typography>
                                        {userData.referral_earnings !== null ? (
                                            <Box>
                                                <Typography variant="body1" sx={{ fontWeight: 'medium', }}>
                                                    {transformToBTC(userData.referral_earnings)}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    ${((userData.referral_earnings / 1e11) * btcPrice).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <Typography variant="body1" sx={{ fontWeight: 'medium', }}>
                                                {t('user.notAvailable')}
                                            </Typography>
                                        )}
                                    </Box>
                                </Grid>

                                {/* Referral Count */}
                                <Grid item xs={12} sm={6}>
                                    <Box sx={{ textAlign: 'left' }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            {t('user.referralCount')}:
                                        </Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 'medium', }}>
                                            {userData.referral_count !== null ? userData.referral_count.toLocaleString() : t('user.notAvailable')}
                                        </Typography>
                                    </Box>
                                </Grid>

                                {/* BTC Deposit Address */}
                                <Grid item xs={12} sm={6}>
                                    <Box sx={{ textAlign: 'left' }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            {t('user.btcDepositAddress')}:
                                        </Typography>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <Typography variant="body1" sx={{ wordBreak: 'break-all', }}>
                                                {truncateAddress(userData.btc_deposit_address) || t('user.notAvailable')}
                                            </Typography>
                                            {userData.btc_deposit_address && (
                                                <Tooltip title={t('user.copyBTCDepositAddress')}>
                                                    <IconButton size="small" onClick={() => copyToClipboard(userData.btc_deposit_address, t('user.btcDepositAddress'))} sx={{}}>
                                                        <ContentCopyIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Stack>
                                    </Box>
                                </Grid>

                                {/* BTC Wallet Address */}
                                <Grid item xs={12} sm={6}>
                                    <Box sx={{ textAlign: 'left' }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            {t('user.btcWalletAddress')}:
                                        </Typography>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <Typography variant="body1" sx={{ wordBreak: 'break-all', }}>
                                                {truncateAddress(userData.btc_wallet_address) || t('user.notAvailable')}
                                            </Typography>
                                            {userData.btc_wallet_address && (
                                                <Tooltip title={t('user.copyBTCWalletAddress')}>
                                                    <IconButton size="small" onClick={() => copyToClipboard(userData.btc_wallet_address, t('user.btcWalletAddress'))} sx={{}}>
                                                        <ContentCopyIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Stack>
                                    </Box>
                                </Grid>

                                {/* RUNE Deposit Address */}
                                <Grid item xs={12} sm={6}>
                                    <Box sx={{ textAlign: 'left' }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            {t('user.runeDepositAddress')}:
                                        </Typography>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <Typography variant="body1" sx={{ wordBreak: 'break-all', }}>
                                                {truncateAddress(userData.rune_deposit_address) || t('user.notAvailable')}
                                            </Typography>
                                            {userData.rune_deposit_address && (
                                                <Tooltip title={t('user.copyRUNEDepositAddress')}>
                                                    <IconButton size="small" onClick={() => copyToClipboard(userData.rune_deposit_address, t('user.runeDepositAddress'))} sx={{}}>
                                                        <ContentCopyIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Stack>
                                    </Box>
                                </Grid>

                                {/* Created At */}
                                <Grid item xs={12} sm={6}>
                                    <Box sx={{ textAlign: 'left' }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            {t('user.memberSince')}:
                                        </Typography>
                                        <Typography variant="body1" sx={{}}>{formattedCreatedAt}</Typography>
                                    </Box>
                                </Grid>

                                {/* Ref Code */}
                                <Grid item xs={12} sm={6}>
                                    <Box sx={{ textAlign: 'left' }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            {t('user.referralCode')}:
                                        </Typography>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <Typography variant="body1" sx={{}}>{userData.ref_code || t('user.notAvailable')}</Typography>
                                            {userData.ref_code && (
                                                <Tooltip title={t('user.copyReferralCode')}>
                                                    <IconButton size="small" onClick={() => copyToClipboard(userData.ref_code, t('user.referralCode'))} sx={{}}>
                                                        <ContentCopyIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Stack>
                                    </Box>
                                </Grid>
                            </Grid>

                            {/* Other null/less critical fields can be added here if needed */}
                            <Grid container spacing={1} sx={{ width: '100%', mb: 1 }}>
                                <Grid item xs={12} sm={6}>
                                    <Box sx={{ textAlign: 'left' }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            {t('user.referrer')}:
                                        </Typography>
                                        {/* <Typography variant="body1" sx={{}}>{userData.referrer || 'N/A'}</Typography> */}
                                        <Typography variant="body1" sx={{
                                            "&:hover": {
                                                textDecoration: userData.referrer ? 'underline' : 'none',
                                                cursor: userData.referrer ? 'pointer' : 'default',
                                                color: userData.referrer ? theme.palette.primary.main : 'inherit'
                                            }
                                        }}
                                            onClick={() => {
                                                if (userData.referrer) {
                                                    window.open(`/users/${ userData.referrer }`);
                                                }
                                            }}
                                        >{userData.referrer ? truncateAddress(userData.referrer, 10, 10) : t('user.notAvailable')}</Typography>

                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Box sx={{ textAlign: 'left' }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            {t('user.blifeId')}:
                                        </Typography>
                                        <Typography variant="body1" sx={{}}>{userData.blife_id || t('user.notAvailable')}</Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </>
                    )
                }
            </Paper>

            {/* Snackbar for copy notifications */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>

            {/* PNL Card Dialog */}
            <PNLCard
                open={pnlCardOpen}
                onClose={() => setPnlCardOpen(false)}
                userId={userId}
            />
        </>
    );
};

export default UserHeader;